/**
 * ============================================================================
 * GET DCR SUMMARY API — accessor for POST /getDcrSummary.
 * ============================================================================
 *
 * LAYER: accessor. Transport for the Dive Deep DCR tab's lifetime table.
 * Streams pages through followPaginationChain so the caller can render the
 * first page while the rest load (spec dive-deep-dcr-so R2.2). Cache policy
 * lives in the RTK endpoint (features/api/lifetimeSummaryApi.ts).
 */

import { ReportingApiClient } from 'src/accessors/ReportingApiClient';
import { followPaginationChain } from 'src/accessors/followPaginationChain';
import {
  DcrSummaryRecord,
  GetDcrSummaryInput,
  GetDcrSummaryOutput,
} from 'src/types/lifetimeSummary.types';

/** Maximum the model allows (SummaryPageSize range 1–100); fewest chains. */
export const SUMMARY_PAGE_SIZE = 100;

class GetDcrSummaryApiClient extends ReportingApiClient {
  /** One page, exactly as the backend returns it. */
  public async getDcrSummaryPage(input: GetDcrSummaryInput): Promise<GetDcrSummaryOutput> {
    return this.post<GetDcrSummaryOutput>('/getDcrSummary', input);
  }

  /**
   * Streams every page for the scope: onPage fires per page with that page's
   * records. `startKey` resumes a failed chain from its last good cursor.
   */
  public async streamAllPages(
    input: GetDcrSummaryInput,
    onPage: (records: DcrSummaryRecord[], pageNo: number, lastRefreshedAt: string) => void,
    startKey?: string,
  ): Promise<void> {
    const scoped: GetDcrSummaryInput = { ...input, pageSize: input.pageSize ?? SUMMARY_PAGE_SIZE };
    await followPaginationChain(
      'GetDcrSummary',
      async (paginationKey) => {
        const page = await this.getDcrSummaryPage(
          paginationKey ? { ...scoped, paginationKey } : scoped,
        );
        return { rows: page.data ?? [], paginationKey: page.paginationKey, lastRefreshedAt: page.lastRefreshedAt };
      },
      onPage,
      startKey,
    );
  }
}

/** Singleton (portal convention): one shared instance app-wide. */
export const GetDcrSummaryApi = new GetDcrSummaryApiClient();
